import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrafficBadge, SeverityBadge } from "@/components/Atoms";
import SignatureCanvas from "react-signature-canvas";
import { FilePdf, EnvelopeSimple, Sparkle, MapPin, Camera, Lock, Trash, Plus, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function InspectionDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [insp, setInsp] = useState(null);
    const [tab, setTab] = useState("info");
    const [reload, setReload] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/inspections/${id}`);
                setInsp(r.data);
            } catch (e) { if (e.response?.status === 404) nav("/inspections"); }
        })();
    }, [id, reload, nav]);

    if (!insp) return <div className="p-8 text-center text-zinc-500">Cargando…</div>;
    const refresh = () => setReload(r => r + 1);
    const isTecnicoOwner = user?.role === "tecnico" && user?.cedula === insp.technician_cedula;
    const canEdit = isTecnicoOwner || user?.role === "supervisor" || user?.role === "admin";
    const closed = !!insp.fecha_salida;

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="inspection-detail-page">
            <button onClick={() => nav(-1)} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="back-btn">
                <ArrowLeft size={14} /> Volver
            </button>
            <div className="mb-8 border-b border-zinc-200 pb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-xs text-zinc-500 mb-1">{insp.inspection_id} · Orden {insp.order_number}</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">{insp.client?.razon_social || "—"}</h1>
                    <div className="text-sm text-zinc-600 mt-1">{insp.sistema_revisado} · <span className="font-semibold">{insp.norma_iso}</span></div>
                </div>
                <div className="flex items-center gap-3">
                    <TrafficBadge value={insp.estado_salud} />
                    <span className={`badge-sev ${closed ? "sev-verde" : "sev-media"}`}>{closed ? "CERRADA" : "EN CURSO"}</span>
                </div>
            </div>

            <div className="flex gap-0 border border-zinc-200 mb-8" data-testid="tabs">
                {[
                    { v: "info", label: "Información" },
                    { v: "findings", label: `Hallazgos (${insp.findings?.length || 0})` },
                    { v: "signatures", label: "Firmas" },
                    { v: "report", label: "Acta + Email" },
                ].map(t => (
                    <button key={t.v} onClick={() => setTab(t.v)} className={`flex-1 px-3 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider border-r border-zinc-200 last:border-r-0 transition-colors ${tab === t.v ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"}`} data-testid={`tab-${t.v}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "info" && <InfoTab insp={insp} canEdit={canEdit} closed={closed} onRefresh={refresh} />}
            {tab === "findings" && <FindingsTab insp={insp} canEdit={canEdit && !closed} onRefresh={refresh} />}
            {tab === "signatures" && <SignaturesTab insp={insp} canEdit={canEdit && !closed} onRefresh={refresh} />}
            {tab === "report" && <ReportTab insp={insp} onRefresh={refresh} />}
        </div>
    );
}

function InfoTab({ insp, canEdit, closed, onRefresh }) {
    const [closing, setClosing] = useState(false);
    const [closeError, setCloseError] = useState("");
    const firmaTecOk = !!insp.firma_tecnico && String(insp.firma_tecnico).startsWith("data:image");
    const firmaCliOk = !!insp.firma_cliente && String(insp.firma_cliente).startsWith("data:image");
    const firmasOk = firmaTecOk && firmaCliOk;

    const cerrar = async () => {
        setCloseError("");
        if (!firmasOk) {
            setCloseError("Falta capturar las firmas antes de cerrar. Ve a la pestaña 'Firmas'.");
            return;
        }
        if (!window.confirm("¿Cerrar acta de inspección? Se calculará el semáforo final, generará el diagnóstico IA consolidado y bloqueará la edición.")) return;
        setClosing(true);
        try {
            await api.patch(`/inspections/${insp.inspection_id}`, { cerrar: true });
            onRefresh();
        } catch (e) {
            setCloseError(e.response?.data?.detail || "Error al cerrar");
        }
        setClosing(false);
    };
    return (
        <div data-testid="info-tab">
            <div className="grid lg:grid-cols-3 gap-0 border border-zinc-200 mb-6">
                <Row label="Cliente" value={insp.client?.razon_social} />
                <Row label="NIT" value={insp.client?.nit} mono />
                <Row label="Encargado en sitio" value={insp.order?.encargado_nombre} />
                <Row label="Sistema revisado" value={insp.sistema_revisado} />
                <Row label="Norma aplicable" value={insp.norma_iso} />
                <Row label="N° de orden" value={insp.order_number} mono />
                <Row label="Técnico" value={insp.technician_name} />
                <Row label="Cédula técnico" value={insp.technician_cedula} mono icon={<Lock size={11} />} />
                <Row label="ID inspección" value={insp.inspection_id} mono />
                <Row label="Fecha ingreso" value={insp.fecha_ingreso?.slice(0, 19).replace("T", " ")} mono icon={<Lock size={11} />} />
                <Row label="Fecha salida" value={insp.fecha_salida ? insp.fecha_salida.slice(0, 19).replace("T", " ") : "En curso"} mono icon={<Lock size={11} />} />
                <Row label="GPS captura" value={insp.gps_lat != null ? `${Number(insp.gps_lat).toFixed(5)}, ${Number(insp.gps_lng).toFixed(5)}` : "—"} mono icon={<MapPin size={11} />} />
            </div>

            {insp.geofence_ok === false && (
                <div className="border-l-4 p-4 mb-6" style={{ background: "#FEE2E2", borderColor: "#EF4444" }} data-testid="geofence-alert">
                    <div className="flex items-start gap-3">
                        <span style={{ color: "#991B1B" }} className="text-2xl">⚠</span>
                        <div>
                            <div className="font-bold text-sm text-red-900">Alerta de geofencing</div>
                            <div className="text-xs text-red-800 mt-1">
                                El técnico se encuentra a <span className="font-mono font-bold">{insp.geofence_distance_m} m</span> de la sede registrada del cliente.
                                Esta distancia excede el radio permitido. Validación requiere atención del supervisor.
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {insp.geofence_ok === true && (
                <div className="border-l-4 p-3 mb-6" style={{ background: "#D1FAE5", borderColor: "#10B981" }} data-testid="geofence-ok">
                    <div className="text-xs text-emerald-900">
                        ✓ Geofencing OK: técnico a <span className="font-mono font-bold">{insp.geofence_distance_m} m</span> de la sede registrada.
                    </div>
                </div>
            )}

            {insp.diagnostico_ia && (
                <div className="bg-zinc-950 text-white p-6 mb-6 grain-overlay relative" data-testid="diagnostico-ia">
                    <div className="overline mb-3 flex items-center gap-2" style={{ color: "rgba(94, 234, 212, 0.8)" }}>
                        <Sparkle size={12} weight="fill" /> Diagnóstico consolidado del Arquitecto Supervisor (por medio de IA)
                        {insp.diagnostico_ia_ok === false && (
                            <span className="text-[10px] px-1.5 py-0.5" style={{ background: "rgba(239,68,68,0.2)", color: "#FCA5A5" }}>FALLBACK</span>
                        )}
                    </div>
                    <pre className="text-sm leading-relaxed whitespace-pre-wrap font-body">{insp.diagnostico_ia}</pre>
                </div>
            )}

            {canEdit && !closed && (
                <div className="border border-zinc-200 p-6 bg-zinc-50" data-testid="cierre-block">
                    <div className="overline mb-2">Cierre de inspección</div>
                    <p className="text-sm text-zinc-600 mb-4 max-w-2xl">
                        Al cerrar, el sistema calculará automáticamente el semáforo final basado en las severidades de los hallazgos, generará el diagnóstico IA consolidado y bloqueará la fecha de salida. La orden de servicio quedará marcada como cerrada.
                    </p>
                    {/* Status de firmas obligatorias */}
                    <div className="grid grid-cols-2 gap-3 mb-4 max-w-xl" data-testid="firmas-status">
                        <div className={`p-3 border ${firmaTecOk ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`}>
                            <div className="text-[10px] font-mono font-bold" style={{ color: firmaTecOk ? "#065F46" : "#92400E" }}>
                                {firmaTecOk ? "✓" : "✗"} FIRMA TÉCNICO
                            </div>
                            <div className="text-xs text-zinc-600 mt-0.5">{firmaTecOk ? "Guardada" : "Pendiente"}</div>
                        </div>
                        <div className={`p-3 border ${firmaCliOk ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`}>
                            <div className="text-[10px] font-mono font-bold" style={{ color: firmaCliOk ? "#065F46" : "#92400E" }}>
                                {firmaCliOk ? "✓" : "✗"} FIRMA CLIENTE
                            </div>
                            <div className="text-xs text-zinc-600 mt-0.5">{firmaCliOk ? "Guardada" : "Pendiente"}</div>
                        </div>
                    </div>
                    {closeError && <div className="mb-3 p-3 border border-red-300 bg-red-50 text-xs text-red-900" data-testid="close-error">{closeError}</div>}
                    <button onClick={cerrar} disabled={closing || !firmasOk} className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="cerrar-inspeccion" title={firmasOk ? "" : "Debes capturar ambas firmas primero"}>
                        <CheckCircle size={16} weight="fill" /> {closing ? "Cerrando + IA…" : firmasOk ? "Firmar y cerrar acta" : "Faltan firmas para cerrar"}
                    </button>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, mono, icon }) {
    return (
        <div className="p-4 border-r border-b border-zinc-200 last:border-r-0">
            <div className="overline mb-1.5 flex items-center gap-1.5">{icon}{label}</div>
            <div className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
        </div>
    );
}

function FindingsTab({ insp, canEdit, onRefresh }) {
    const [showForm, setShowForm] = useState(false);
    return (
        <div data-testid="findings-tab">
            <div className="flex items-center justify-between mb-4">
                <div className="overline">Hallazgos registrados</div>
                {canEdit && (
                    <button onClick={() => setShowForm(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-add-finding">
                        <Plus size={14} weight="bold" /> {showForm ? "Cancelar" : "Añadir hallazgo"}
                    </button>
                )}
            </div>
            {showForm && <AddFindingForm inspectionId={insp.inspection_id} onAdded={() => { setShowForm(false); onRefresh(); }} />}
            <div className="space-y-4 mt-6">
                {(insp.findings || []).length === 0 && <div className="p-12 text-center text-zinc-500 border border-dashed border-zinc-300">Sin hallazgos registrados.</div>}
                {(insp.findings || []).map(f => <FindingCard key={f.finding_id} f={f} canEdit={canEdit} onRefresh={onRefresh} />)}
            </div>
        </div>
    );
}

function AddFindingForm({ inspectionId, onAdded }) {
    const [form, setForm] = useState({ titulo: "", descripcion: "", severidad: "media" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try { await api.post(`/inspections/${inspectionId}/findings`, form); onAdded(); }
        catch (err) { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="border border-zinc-200 p-6 bg-zinc-50" data-testid="add-finding-form">
            <div className="overline mb-4" style={{ color: "#0F766E" }}>
                Paso 1 · Registra la observación. Luego adjunta foto y solicita la Recomendación del Arquitecto Supervisor.
            </div>
            <div className="space-y-4">
                <input className="input-atia" placeholder="Título del hallazgo" required value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} data-testid="finding-titulo" spellCheck="true" lang="es-CO" autoCapitalize="sentences" />
                <textarea className="input-atia" rows={3} placeholder="Observación detallada del técnico (qué viste, qué mediste, condiciones)…" required value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} data-testid="finding-descripcion" spellCheck="true" lang="es-CO" autoCorrect="on" autoCapitalize="sentences" />
                <div className="flex gap-0 border border-zinc-200 max-w-md">
                    {["baja", "media", "alta", "critica"].map(s => (
                        <button type="button" key={s} onClick={() => setForm({ ...form, severidad: s })} className={`flex-1 px-3 py-2 text-xs font-bold uppercase border-r last:border-r-0 border-zinc-200 transition-colors ${form.severidad === s ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"}`} data-testid={`finding-sev-${s}`}>{s}</button>
                    ))}
                </div>
                <button type="submit" disabled={loading} className="btn-accent" data-testid="submit-finding">
                    {loading ? "Guardando…" : "Guardar hallazgo (sin recomendación aún)"}
                </button>
            </div>
        </form>
    );
}

function FindingCard({ f, canEdit, onRefresh }) {
    const [uploading, setUploading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const fileRef = useRef(null);
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        setUploading(true);
        try { await api.post(`/findings/${f.finding_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onRefresh(); }
        catch (err) { console.error(err); } finally { setUploading(false); }
    };
    const generateReco = async () => {
        setGenerating(true);
        try { await api.post(`/findings/${f.finding_id}/generate-recommendation`); onRefresh(); }
        catch (err) { console.error(err); } finally { setGenerating(false); }
    };
    const ready = f.foto_path && f.descripcion;
    return (
        <div className="border border-zinc-200 p-5" data-testid={`finding-card-${f.finding_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="flex gap-4">
                    {f.foto_path ? (
                        <img src={`${API}/findings/${f.finding_id}/photo`} alt="" className="w-28 h-28 object-cover border border-zinc-200 flex-shrink-0" data-testid={`thumb-${f.finding_id}`} />
                    ) : (
                        <div className="w-28 h-28 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 flex-shrink-0"><Camera size={24} /></div>
                    )}
                    <div>
                        <div className="font-mono text-[10px] text-zinc-500">{f.finding_id}</div>
                        <h3 className="font-display text-xl font-bold tracking-tight">{f.titulo}</h3>
                        <p className="text-sm text-zinc-700 leading-relaxed mt-1.5 max-w-2xl">{f.descripcion}</p>
                    </div>
                </div>
                <SeverityBadge value={f.severidad} />
            </div>

            {canEdit && (
                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onUpload} data-testid={`file-input-${f.finding_id}`} />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary text-xs flex items-center gap-1" data-testid={`upload-${f.finding_id}`}>
                        <Camera size={14} /> {uploading ? "Subiendo…" : (f.foto_path ? "Cambiar foto" : "Adjuntar foto")}
                    </button>
                    {ready && (
                        <button onClick={generateReco} disabled={generating} className="btn-accent text-xs flex items-center gap-2" data-testid={`gen-reco-${f.finding_id}`}>
                            <Sparkle size={14} weight="fill" />
                            {generating ? "Consultando Arquitecto Supervisor…" : (f.recomendacion_ia ? "Regenerar recomendación" : "Solicitar recomendación del Arquitecto Supervisor")}
                        </button>
                    )}
                    {!f.foto_path && (
                        <span className="text-[11px] text-zinc-500">Adjunta una foto para habilitar la recomendación →</span>
                    )}
                </div>
            )}

            {f.recomendacion_ia && (
                <div className="bg-zinc-950 text-white p-4 mt-4 grain-overlay relative" data-testid={`reco-${f.finding_id}`}>
                    <div className="overline mb-2 flex items-center gap-1.5" style={{ color: "rgba(94, 234, 212, 0.8)" }}>
                        <Sparkle size={10} weight="fill" /> Recomendación del Arquitecto Supervisor (por medio de IA)
                        {f.recomendacion_ia_ok === false && (
                            <span className="text-[9px] px-1.5 py-0.5" style={{ background: "rgba(239,68,68,0.2)", color: "#FCA5A5" }}>FALLBACK</span>
                        )}
                    </div>
                    <pre className="text-xs leading-relaxed whitespace-pre-wrap font-body">{f.recomendacion_ia}</pre>
                </div>
            )}
        </div>
    );
}

function SignaturesTab({ insp, canEdit, onRefresh }) {
    return (
        <div className="grid md:grid-cols-2 gap-6" data-testid="signatures-tab">
            <SignaturePad title="Firma del técnico" current={insp.firma_tecnico} canEdit={canEdit}
                onSave={(data) => api.patch(`/inspections/${insp.inspection_id}`, { firma_tecnico: data }).then(onRefresh)}
                testid="signature-tecnico" subtitle={`${insp.technician_name} · CC ${insp.technician_cedula}`} />
            <SignaturePad title="Firma del cliente" current={insp.firma_cliente} canEdit={canEdit}
                onSave={(data) => api.patch(`/inspections/${insp.inspection_id}`, { firma_cliente: data }).then(onRefresh)}
                testid="signature-cliente" subtitle={insp.order?.encargado_nombre || "Representante del cliente"} />
        </div>
    );
}

function SignaturePad({ title, current, onSave, testid, subtitle, canEdit }) {
    const ref = useRef(null);
    const [saving, setSaving] = useState(false);
    const clear = () => ref.current?.clear();
    const save = async () => {
        if (!ref.current || ref.current.isEmpty()) return;
        setSaving(true);
        const data = ref.current.getTrimmedCanvas().toDataURL("image/png");
        await onSave(data); setSaving(false);
    };
    return (
        <div className="border border-zinc-200 p-5" data-testid={testid}>
            <div className="overline mb-1">{title}</div>
            <div className="text-xs text-zinc-500 mb-3">{subtitle}</div>
            {current ? (
                <div>
                    <img src={current} alt="firma" className="w-full h-40 object-contain bg-zinc-50 border border-zinc-200" />
                    {canEdit && <button onClick={() => onSave("")} className="btn-secondary mt-3 w-full" data-testid={`${testid}-redo`}>Volver a firmar</button>}
                </div>
            ) : canEdit ? (
                <>
                    <div className="border border-zinc-200">
                        <SignatureCanvas ref={ref} penColor="#000" canvasProps={{ className: "signature-canvas", "data-testid": `${testid}-canvas` }} />
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button onClick={clear} className="btn-secondary flex items-center gap-2" data-testid={`${testid}-clear`}><Trash size={14} /> Limpiar</button>
                        <button onClick={save} disabled={saving} className="btn-accent flex-1" data-testid={`${testid}-save`}>{saving ? "Guardando…" : "Guardar firma"}</button>
                    </div>
                </>
            ) : (
                <div className="p-6 text-center text-zinc-400 text-sm border border-dashed border-zinc-300">Sin firma registrada</div>
            )}
        </div>
    );
}

function ReportTab({ insp, onRefresh }) {
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const downloadPdf = async () => {
        const r = await api.get(`/inspections/${insp.inspection_id}/pdf`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url; a.download = `acta_${insp.inspection_id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
    };
    const sendEmail = async () => {
        setSending(true);
        try {
            const r = await api.post(`/inspections/${insp.inspection_id}/send_email`);
            setFeedback({ type: "success", msg: `Envío simulado a ${r.data.to}. Asunto: ${r.data.subject}` });
            onRefresh();
        } catch (e) {
            setFeedback({ type: "error", msg: e.response?.data?.detail || "Error" });
        }
        setSending(false);
    };
    return (
        <div className="grid md:grid-cols-2 gap-6" data-testid="report-tab">
            <div className="border border-zinc-200 p-6">
                <FilePdf size={32} className="mb-4" />
                <div className="overline mb-2">Acta de inspección</div>
                <h3 className="font-display text-2xl font-bold mb-2">Descargar PDF</h3>
                <p className="text-sm text-zinc-600 mb-6">Acta conforme a {insp.norma_iso} con cabecera corporativa, trazabilidad, no-conformidades, diagnóstico IA, firmas y QR de verificación.</p>
                <button onClick={downloadPdf} className="btn-primary w-full" data-testid="download-pdf">Descargar PDF →</button>
            </div>
            <div className="border border-zinc-200 p-6">
                <EnvelopeSimple size={32} className="mb-4" />
                <div className="overline mb-2">Envío al cliente</div>
                <h3 className="font-display text-2xl font-bold mb-2">Email automático</h3>
                <p className="text-sm text-zinc-600 mb-2">Destinatario: <span className="font-mono">{insp.order?.email_informe || insp.client?.email || "—"}</span></p>
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2 mb-4">MODO DEMO: el envío se registra en logs (sin SMTP real).</p>
                <button onClick={sendEmail} disabled={sending} className="btn-accent w-full" data-testid="send-email">{sending ? "Enviando…" : "Enviar acta por email"}</button>
                {feedback && <div className={`mt-4 p-3 text-xs ${feedback.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-red-50 border border-red-200 text-red-800"}`} data-testid="email-feedback">{feedback.msg}</div>}
            </div>
        </div>
    );
}
